const GECKO_TERMINAL_BASE_URL = 'https://api.geckoterminal.com/api/v2';
const NETWORK = 'base';
const POOL_ADDRESS = '0x01c1cff07a7f967fd7613088bb1712554944c6e6';

const TIMEFRAME_MAP: Record<string, string> = {
  '1H': 'minute',
  DAY: 'hour',
  WEEK: 'hour',
  '1M': 'day',
  YEAR: 'day',
  ALL: 'day',
};

const AGGREGATE_MAP: Record<string, string> = {
  '1H': '1',
  DAY: '1',
  WEEK: '4',
  '1M': '1',
  YEAR: '1',
  ALL: '1',
};

const LIMIT_MAP: Record<string, number> = {
  '1H': 60,
  DAY: 24,
  WEEK: 42,
  '1M': 30,
  YEAR: 365,
  ALL: 1000,
};

export interface PriceDataPoint {
  timestamp: number;
  price: number;
}

export interface TokenInfo {
  address: string;
  name: string;
  symbol: string;
  price_usd: string;
}

export interface PoolData {
  base_token_price_usd: string;
  fdv_usd: string;
  price_change_percentage: {
    h24: string;
  };
}

export async function fetchTokenPriceData(
  timeRange: string,
): Promise<PriceDataPoint[]> {
  try {
    const timeframe = TIMEFRAME_MAP[timeRange] || 'day';
    const aggregate = AGGREGATE_MAP[timeRange] || '1';
    const limit = LIMIT_MAP[timeRange] || 100;

    const url = `${GECKO_TERMINAL_BASE_URL}/networks/${NETWORK}/pools/${POOL_ADDRESS}/ohlcv/${timeframe}?aggregate=${aggregate}&limit=${limit}`;

    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('OHLCV fetch failed:', response.status, errorText);
      throw new Error(`GeckoTerminal API error: ${response.statusText}`);
    }

    const data = await response.json();

    const ohlcvData = data.data?.attributes?.ohlcv_list || [];

    const priceData: PriceDataPoint[] = ohlcvData
      .filter((candle: number[]) => candle && candle.length >= 5)
      .map((candle: number[]) => ({
        timestamp: (candle[0] ?? 0) * 1000,
        price: candle[4] ?? 0,
      }));

    return priceData;
  } catch (error) {
    console.error('Error fetching token price data:', error);
    return [];
  }
}

export async function fetchTokenInfo(): Promise<TokenInfo | null> {
  try {
    const response = await fetch(
      `${GECKO_TERMINAL_BASE_URL}/networks/${NETWORK}/pools/${POOL_ADDRESS}`,
      {
        headers: {
          Accept: 'application/json',
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch pool info: ${response.statusText}`);
    }

    const data = await response.json();
    const attributes = data.data?.attributes;

    return {
      address: POOL_ADDRESS,
      name: attributes?.name || 'UNA',
      symbol: 'UNA',
      price_usd: attributes?.base_token_price_usd || '0',
    };
  } catch (error) {
    console.error('Error fetching pool info:', error);
    return null;
  }
}

export async function fetchPoolData(): Promise<PoolData> {
  const response = await fetch(
    `${GECKO_TERMINAL_BASE_URL}/networks/${NETWORK}/pools/${POOL_ADDRESS}`,
    {
      headers: {
        Accept: 'application/json',
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch pool data: ${response.statusText}`);
  }

  const data = await response.json();
  return data.data.attributes;
}
